// @refresh reset — dev-only: force remount so board nodes hot-reload on edit
import { lazy, Suspense, useMemo } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { OutformErd } from "./command/outform-erd";
// ⭐ ONE ENTITY DIAGRAM PER JOB (Erez, 30 Aug): "I want an entity diagram under
// each job." The five `SheetGraph_*` were per FAMILY, so four jobs shared
// `converting` and nothing showed 244524's, 255401's or 261152's own wiring.
// Generated by scratch/gen_sheetgraph_perjob.py from each job's OWN authoritative
// workbook — 250010 now from workbook C, not B.
import { Entity240266 } from "./command/outform-entity-240266";
import { Entity243854 } from "./command/outform-entity-243854";
import { Entity244524 } from "./command/outform-entity-244524";
import { Entity250010 } from "./command/outform-entity-250010";
import { Entity255401 } from "./command/outform-entity-255401";
import { Entity261152 } from "./command/outform-entity-261152";
import { Entity261801 } from "./command/outform-entity-261801";
import { Entity264714 } from "./command/outform-entity-264714";
import { SheetGraph_converting } from "./command/outform-sheets-converting";
import { SheetGraph_merit } from "./command/outform-sheets-merit";
import { SheetGraph_lg } from "./command/outform-sheets-lg";
import { SheetGraph_clarity } from "./command/outform-sheets-clarity";
import { SheetGraph_morphe } from "./command/outform-sheets-morphe";
import { PresentationFrame } from "./PresentationFrame";
import { DocumentFrame } from "./DocumentFrame";
import { ProjectFrame } from "./ProjectFrame";
import { TicketsKanbanPage } from "../tickets/TicketsKanbanPage";
import { Outform264714LayersDiagram } from "./command/outform-264714-layers";
import {
  Job243854Diagram, Job244524Diagram, Job255401Diagram, Job261152Diagram, Job240266Diagram,
  Job250010Diagram, Job261801Diagram, Job264714Diagram, Job264511Diagram, Job253842Diagram, Job264013Diagram,
} from "./command/outform-job-anatomy";
import { OutformCoverage } from "./outform/coverage";
import { OutformSkillMap } from "./outform/skill-map";
import { OutformPricingSheet } from "./outform/calculator";
import { OutformProductUX } from "./outform/proposal-ux";
import { OutformWorkbooks } from "./outform/workbook";
import { OutformFileGrid } from "./outform/files";
import { ShapesInspiration } from "./outform/shapes";
import { OutformTemplateV4 } from "./outform/template-v4";
import { ComparablesFlow } from "./outform/comparables-flow";
import { DeckFrame } from "./DeckFrame";
// The pipeline diagram is ATLAS'S component, rendered here rather than copied —
// one component on two boards cannot drift; two drawings of one pipeline would.
import { DataProgrammeDiagram } from "./command/data-programme";
import { OutformElevenDiagram } from "./command/outform-eleven";
// The workbook read as SOFTWARE rather than as data — Erez, 29 Aug: "their
// excels are acting as a full software ... I need full visibility". Both
// diagrams are measurements over all 29 .xlsx files, not impressions.
import { OutformWorkbookAnatomy, OutformCloneDrift } from "./command/outform-workbook-anatomy";
// design-versions.tsx is imported STATICALLY, not lazily like the chunk
// below — it only needs to be, because the six heavy app-shells inside it
// (two carrying their own Univer instance) are EACH ALREADY behind their own
// lazy(() => import(...)) one file down. Importing this file eagerly costs
// six cheap `lazy()` calls and a titles array; it does not eagerly load any
// of the six chunks. OUTFORM_DESIGN_VERSIONS supplies the six SectionBox
// titles below; OutformDesignVersionScreen renders exactly one version.
import { OUTFORM_DESIGN_VERSIONS, OutformDesignVersionScreen } from "./outform/design-versions";
// LAZY, and not for elegance: eagerly importing this module reproducibly OOM-
// killed `vite build` at "rendering chunks" on the Tailormind box — three runs
// dead, the same tree without it green in 1m15s. As its own chunk it never
// joins the (already >500 kB) main bundle, and a board section nobody has
// scrolled to has no business being in the first byte the app loads anyway.
// Univer is 5.5 MB of grid + formula engine. Lazy is not optional here.
const OutformUniverSheet = lazy(() =>
  import("./outform/univer-sheet").then((m) => ({ default: m.OutformUniverSheet })),
);

/** A forced row break inside <SectionGrid>'s flex-wrap. Zero height, full
 *  width — so the next box starts a new line regardless of how much room is
 *  left. Same device the Falcon and Marketing boards use to pin their rows.
 *
 *  ⚠️ CURRENTLY UNUSED, AND THAT IS THE POINT (Erez, 21 Aug): "it's just a long
 *  thing. Let's consolidate and put more items in each row, make it more like a
 *  wide screen at high level." There was a <Break /> after almost every section,
 *  which pinned the board to ONE SECTION PER ROW — sixteen sections, sixteen
 *  rows, a column pretending to be a board. Removing them lets the flex-wrap do
 *  its job: SectionGrid is 6000 wide and a 2440 design section boxes out at
 *  2860 (210 padding a side), so two sit side by side with room for the gap,
 *  and the smaller sections pack three or four across.
 *
 *  Kept rather than deleted because it is the right tool the day one band
 *  genuinely must start on a fresh row — but reach for it deliberately, never
 *  by habit. */
const Break = () => <div style={{ flexBasis: "100%", height: 0 }} />;

// ─────────────────────────────────────────────────────────────────────────────
// THE OUTFORM BOARD — engagement surface for Outform, a US retail-fixture job
// shop that designs and builds in-store displays.
//
// STATUS: LIVE PROJECT. Sold 12 August 2026. Kickoff Tuesday 18 August.
//
// ── REBUILT 15 AUGUST 2026 · NINE SECTIONS TO SIX, IN TWO PASSES ────────────
// Erez: "I feel like it's becoming a mess. We have all of these discovery
// documents, we have the rebuild plan, then the concept, then how they estimate,
// then architecture options... Discovery as the top section. It has the
// blueprint, it has the product and architecture document next to it, the
// roadmap, the answers to Andy. And anything else we don't need, we can delete."
//
// He was right, and the mess was mine: I had been ADDING a section per artefact
// and never merging. Nine sections and sixteen documents for one engagement.
//
// WHAT WENT, WITH HIS WORD FOR EACH:
//   · "How they estimate" and "Architecture options" — both diagrams, and their
//     code (estimating-flow.tsx, architecture-options.tsx). His verdict: "the
//     diagrams we created I think we can delete, they're just not correct and
//     they're just noise." A diagram that has stopped being true is worse than
//     no diagram, because it is read and believed.
//   · "The requirements film" — it narrates the August demo, and the concept
//     says in as many words that the demo does not represent the concept.
//   · Five KB pages merged into ONE: the concept + what they actually want + the
//     application rebuild plan + the blueprint coverage → `product-and-
//     architecture`. Plus `build-program` and `engagement-state`, both stale.
//     Every retired slug has a row in `page_redirects`, so nothing that ever
//     linked to them breaks.
//
// SECOND PASS, the same afternoon, after he looked at Otto's Falcon board:
// "at the top we have action items, I want to create the same thing for
// Outform... then the project docs... and the blueprint should be in a
// discovery section underneath those two."
//
// So the order is now the argument: what are we DOING · the four canonical
// documents · what they gave us · what we are building · what it costs · and
// whether it is a product for anyone else. Action items first, because it is
// the only section that answers "what next" — everything under it is reference.
//
// Board standard: BoardCanvas + SectionBox/SectionGrid, one grid node, packed
// into a wide rectangle. Owner agent: Quinn. Jarvis validates.
// ─────────────────────────────────────────────────────────────────────────────

const T = { bg: "#E7E5E1", dots: "#D2CFC9" };

// Their 24 pages + the PDF, on our R2.
const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/outform/blueprint";

// The REAL page ratio of the source document (US Letter, 1275x1650 at 150dpi).
// PresentationFrame defaults to 16:9; a PORTRAIT document passed 16:9 would be
// fitted by height and shrink to a stamp between two grey margins.
const PAGE_ASPECT = 1275 / 1650;

// ── THE STANDARD SHEET. ONE SIZE, EVERY DOCUMENT ON THIS BOARD ──────────────
// Erez, 17 Aug: "we have a standard size for PDFs we show on the page. For some
// reason in Outform we're not keeping it. Project docs are the right size;
// Discovery is not, and the cards you created are not."
//
// He was right and there were THREE sizes on one board: Project docs hardcoded
// 880x1240 (the one he approves of), Discovery on a DOC_W/DOC_H of 900x1530,
// and the nine build pages I had just added at 720x560. A "standard" that three
// sections each write their own copy of is not a standard — it is a coincidence
// waiting to drift, which is exactly what happened.
//
// So this constant is now the ONLY place a sheet size is written, every frame
// on the board reads it, and the value is the one he pointed at.
const DOC_W = 880;
const DOC_H = 1240;

// ── THE STANDARD SCREEN. ONE SIZE, EVERY LIVE PROTOTYPE ON THIS BOARD ───────
// Erez, 23 Aug: "we need to organize the board... first of all we need to
// standardize sizes of screens... let's just go with the action item as the
// standard size." Action items is <TicketsKanbanPage embedded />, which sets
// its OWN width at 1710 (three kanban columns + the fixed-width detail pane —
// see the comment on that component's embedded style). That number is not
// duplicated here; it is read into this constant so there is exactly one
// place a screen size is written, same discipline as DOC_W/DOC_H above.
//
// Product prototype and Univer prototype both used to hardcode 2300 — a third
// size nobody chose, just the width that happened to fit when each was built.
// ⚠️ There was a SCREEN_H beside this, derived the same way, and it existed for
// exactly one caller: Univer, which renders to a <canvas> and needs an explicit
// pixel height. Univer moved into "Working on now" on 28 Aug and takes NOW_H
// there, so SCREEN_H had no callers left and is deleted rather than kept "in
// case" — a constant nothing reads is a number the next person has to work out
// whether to trust. The design versions never needed it; they read SCREEN_W and
// scale themselves.
// ⚠️ 1710 → 1580 on 27 Aug, and the number is arithmetic, not taste. Erez asked
// for Story 3 "right next to" Story 2, and SectionGrid flex-wraps against a
// 6400 grid: three boxes cost 3×(W + 420 padding) + 2×200 gap, so W=1710 gives
// 6790 and wraps 2+1, while W=1580 gives 6400 exactly and keeps all three on
// one row. The designs scale (scale = width/APP_W), so this shrinks them ~8%
// rather than reflowing anything. A fourth story will need this re-derived.
const SCREEN_W = 1580;

// ── THE "WORKING ON NOW" ROW — FIVE FRAMES, AND THE WIDTHS ARE ARITHMETIC ────
// Erez, 28 Aug: "let's put all the stuff that we are working on now at the top
// … the internal pitch, the plan end to end internal, just under the action
// items, and then the demo Data Pilot end to end next to it, and then the two
// documents that we now received, and then the Univer example … put them all
// in like one row."
//
// ⚠️ WHY THEY SHARE ONE SectionBox INSTEAD OF BEING FIVE OF THEM, and it is a
// measurement rather than a preference. SectionBox pads 210 a side, so five
// separate boxes on one row cost 5 × 420 = 2100 of padding plus 4 × 170 of gap
// out of a 6400 grid — leaving 724 of CONTENT each, less padding than picture.
// One box pays that padding once: 6400 − 420 = 5980 of usable width, which is
// what makes five readable frames possible at all. It is also the pattern this
// board already uses for Project docs, Emails to send and Their estimating
// workbook — one label, several captioned frames.
//
// ⛔ AND THE GRID CANNOT SIMPLY BE WIDENED TO 10,000 TO AVOID THAT. Chrome will
// not paint a composited layer past ~16,384px and `.react-flow__viewport` IS
// that layer, scaled by zoom — so at MAX_ZOOM 2.5 the paintable width is
// 16,384 / 2.5 = 6,554px. The 6400 grid sits deliberately just inside it (see
// the eleven-frames note further down, which cost an hour when four sheets
// silently vanished on zoom-in). 6400 is a browser limit wearing a constant's
// name; do not raise it to win an argument about layout.
//
// The five widths below sum to exactly 5980 with four 40px gaps. Change one and
// change another, or the row wraps and stops being a row.
const NOW_GAP = 40;
// Erez, 29 Aug: "make it the first document on the left ... push the other stuff
// to the right, because that's what we're working on now." So the handover doc
// leads the row. It is a SIXTH frame and the note below is honest that the five
// no longer sum to 5980: with it the row runs ~6970, which overhangs the 6400
// SectionGrid. That is deliberate and it does not break anything — SectionGrid
// is flex-wrap, so an over-wide SectionBox simply takes its own row and the
// other sections pack around it. The alternative was shrinking all five to fit,
// which would have degraded four working frames to protect an aesthetic.
const NOW_HAND_W = 1200;   // ⭐ delivering-to-an-enterprise — leads the row
const NOW_DECK_W = 1300;   // the internal-plan deck
const NOW_DEMO_W = 1080;   // the Data Pilot read-through
const NOW_TMPL_W = 1240;   // their v4.1 prototype — its own shell is max-width 1240
const NOW_SUMM_W = 900;    // the v2 → v4 change summary
const NOW_UNIV_W = 1300;   // Univer
const NOW_H = 1500;        // one height for the three scrolling frames
// A deck has a fixed aspect; it cannot stretch to NOW_H, so it is derived from
// the 2300 × 1360 it renders at everywhere else on this board.
const NOW_DECK_H = Math.round(1360 * (NOW_DECK_W / 2300));

// ── THE DOCUMENT WE ARE BUILDING, AS NINE PAGES ──────────────────────────────
// Five skills (the architecture Erez locked on 15 Aug — never a sixth without
// him), the two surfaces those skills act through, the questions, and the
// settled ground. Order is deliberate: the skills first because they ARE the
// product, then where their output lands, then what we still do not know.
const BUILD_PAGES: { slug: string; label: string }[] = [
  // FIRST, and added a day later than the rest. §1, §2, §12 and §13 are about
  // the ENGAGEMENT, not about any one skill — and until this page existed there
  // was nowhere to put "replace a one-to-four-day manual process" or "one
  // standard across estimators". So §1 material went into the Ballpark skill by
  // inference, and Erez caught it: three of four rows there cited §1, which
  // never once says Ballpark. This page also holds the holding bay: REQUIRED,
  // owner not yet decided. Guessing the owner is filing an unmade decision
  // where nobody will look for it.
  { slug: "kb-doc/consultancy/outform/the-system", label: "The System · goals, and what is required before we know who owns it" },
  { slug: "kb-doc/consultancy/outform/skills/ballpark-estimate", label: "Skill 1 · Ballpark Estimate" },
  { slug: "kb-doc/consultancy/outform/skills/detailed-estimate", label: "Skill 2 · Detailed Estimate" },
  { slug: "kb-doc/consultancy/outform/skills/vendor-pricing", label: "Skill 3 · Vendor Pricing" },
  { slug: "kb-doc/consultancy/outform/skills/market-price", label: "Skill 4 · Market Price" },
  { slug: "kb-doc/consultancy/outform/skills/estimate-learning", label: "Skill 5 · Estimate Learning" },
  // THE SIXTH, locked by Erez 17 Aug 2026 while reading §1's Visual Cost
  // Intelligence Database. It is the first skill that exists because TWO others
  // need it — Ballpark and Detailed both have to find comparable past jobs, and
  // his rule is that shared logic is not written twice: "if the logic to go and
  // fetch similar jobs from past things is a whole thing by itself, then we
  // shouldn't reproduce this entire logic in both skills."
  { slug: "kb-doc/consultancy/outform/skills/comparable-jobs", label: "Skill 6 · Comparable Jobs · shared, called by Ballpark and Detailed" },
  // THE SEVENTH, locked 18 Aug from §1.2, and the only skill that leaves the
  // building: the pack goes out to vendors and days pass before an answer. It
  // is a step between Ballpark and Detailed rather than a call inside Detailed,
  // because a skill that waits a week is a workflow wearing a skill's name.
  // ⚠ Its TRIGGER is deliberately undecided — Q16, theirs to answer.
  { slug: "kb-doc/consultancy/outform/skills/costing-template", label: "Skill 7 · Costing Template · the vendor RFP, and the only skill that leaves the building" },
  { slug: "kb-doc/consultancy/outform/the-application", label: "The Application · the screens" },
  { slug: "kb-doc/consultancy/outform/the-data", label: "The Data · job record, corpus, connections" },
  { slug: "kb-doc/consultancy/outform/questions", label: "Questions for Outform · the only page that closes" },
  { slug: "kb-doc/consultancy/outform/decisions", label: "Decisions · settled ground, append only" },
];

// ── WHY THIS CONSTANT IS LOAD-BEARING ───────────────────────────────────────
// The custom domain has twice cached the SPA index.html fallback under a hashed
// asset URL (~1.1 KB of text/html where the real bundle is megabytes of
// javascript). /assets/* is immutable for a year and Vite names chunks by
// CONTENT hash, so redeploying identical code reuses the poisoned filename and
// cannot recover. The fix is to change the emitted bytes — and it must be a
// string literal that is actually USED, because an earlier attempt changed only
// comments, which minify away: the bundle came out byte-identical, the hash did
// not move, and the site stayed down.
//
// Bumped to "7" on 15 Aug 2026 with the action-items and project-docs pass.
// Bumped to "9" on 16 Aug 2026 with the mark-up section — this one adds a new
// lazy chunk (pdfjs), so a stale edge entry is worth ruling out.
// Bumped to "50" on 23 Aug 2026 with the screen-size standardization pass.
// Bumped to "51" the same day: the six design versions moved from one
// switcher section into six full-size sections of their own.
// Bumped to "52" the same day: reviewed all six, kept Story 1 + Story 2.
// Bumped to "53" the same day: Story 2 gained the thinking box.
// Bumped to "54" the same day: homepage redesign + sheet-header polish.
// Bumped to "55" the same day: thinking/sheet split into two beats + the
// persistent brief bubble + the home greeting polish.
// Bumped to "56" the same day: sheet styling + analysis, written as real
// in-sheet cells (univer-sheet.tsx), not a separate DOM strip.
// Bumped to "57" on 28 Aug 2026: the "Working on now" row — three sections
// moved into one band under Action items, plus the two files Outform sent that
// morning (their v4.1 prototype as a live iframe, their change log as a KB
// page). This adds a new module, so a stale edge entry is worth ruling out.
const BUILD_SALT = "outform-61";

function GridNode() {
  return (
    <SectionGrid width={6400} gap={170}>
      {/* ── 0 · THE DETAILED INPUT PAGE — the design we are building NEXT.
          Erez, 1 Sept: "planning on the board like the whole page, not in the
          application itself." So it is a DESIGN, presented — board component
          #1, a KB page shown through DocumentFrame — and not code.

          ⛔ REBUILT FROM THE 714 FAMILY ONLY, after he caught me analysing
          four workbooks out of OTHER estimating systems and inventing a
          "families" taxonomy he never asked for: "that's really, really bad.
          I didn't ask you to do that." The chapter now derives every field
          from 264714 and the two sibling books he actually gave me. */}
      {/* ⭐ TWO PAGES, ONE SECTION — Erez, 2 Sept: "that's the document I want
          you to copy and stay within the same section. Put the page next to it
          and adjust it to what I asked."

          I had built the ballpark as a separate section of its own, which was
          the wrong shape: these are the two input surfaces of one product and
          they are read against each other. Same section, same component, same
          frame size — the only thing that differs is which journey it is. */}
      {/* ── THE VENDOR RFQ — Erez, 2 Sept: "research the vendor RFQ, read the
          blueprint, create a ticket and screen design in the board."
          Step 2 of Journey B and the only one that is not a form. Presented as
          a document rather than a live component, matching the change Erez made
          on /command: the KB row is the artefact, the board shows it. */}
      <SectionBox title="Vendor RFQs — the round trip they already do by hand" layout="custom">
        <DocumentFrame
          slug="kb-doc/consultancy/outform/product/31-vendor-rfq"
          label="JOURNEY B · STEP 2 · §3.3's 71 requirements, the screen, the lifecycle, and a schema that models all of it and has never been written to"
          width={1180}
          height={1560}
        />
      </SectionBox>
      <Break />

      <SectionBox title="The two input pages — the designs, from the 714 family" layout="custom">
        <div style={{ display: "flex", gap: 60, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/26-detailed-input-page"
            label="JOURNEY B · Every input on the detailed estimate, derived field-by-field from 264714 and its two siblings — their labels, their vocabulary, the calculation chain behind each"
            width={1180}
            height={1560}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/29-ballpark-input-page"
            label="JOURNEY A · The ballpark, with the historical comparables between the job and the component list — six states, the seven weights behind the score, and the four gaps the code still has"
            width={1180}
            height={1560}
          />
        </div>
      </SectionBox>
      <Break />

      {/* ── 1 · ACTION ITEMS — FIRST, because it is the only section that
          answers "what are we doing next". Everything below it is reference:
          what they gave us, what we are building, what it costs.

          Erez, 15 Aug: "our action items right now is basically a ticket per
          section to go through and make sure that it works correctly in our
          document." So there are FOURTEEN cards — one per section of their
          blueprint, §1 through §13, plus the tool-count defect on the client
          box that had been sitting with a null project_id since 13 August and
          therefore appeared on no board at all.

          §1 and §2 sit in Review because they have been walked with him; the
          other eleven are Todo. **None of them is Done, because only Erez marks
          a section done** — I marked two on my own authority earlier today and
          was correctly pulled up for it.

          This is the same /tickets Kanban, not an Outform component:
          <TicketsKanbanPage project="…" embedded /> is the ONE app, sliced by
          project slug. Clicking a card opens the ticket BESIDE the columns
          rather than navigating away — on a board there is nowhere to navigate
          to without losing the canvas. Drag persists through the same PUT the
          full page uses, so a card moved here is moved everywhere.

          ⚠️ §1's ticket is OUT-14, not OUT-01. `OUT-01` was already taken by a
          real defect ticket from 13 Aug, and renaming someone's existing
          identifier to make a numbering scheme tidy is not worth the risk of
          breaking a reference. The board_order puts it first regardless. */}

      {/* ── 0 · BRIEF FOR SCOTT ─────────────────────────────────────────────
          Jarvis wrote this and asked me to place it, because the board file
          has one owner. Erez is reviewing the Excel with Scott, Outform's main
          estimator, so this is the page he walks in with: the four-engine map,
          one row per job with the single question only an estimator can
          answer, ten business-logic questions, and what one form must hold.

          It sits ABOVE Action items because for the length of that meeting it
          IS the action. Additive — nothing was moved or removed.

          ⚠️ It is a DISTILLATION of the eleven job dossiers, so it inherits
          whatever is stale in them; it measured nothing new. I checked what
          the box can answer: every tab count on it is exact against
          `raw.sheet` (Merit 23, Morphe 19, Clarity 10, LG 20, JCost 26×4).

          ⭐ AND THE RULE THAT CAME OUT OF CHECKING IT, which is Jarvis's
          sharpening of mine and is the better one: A CLAIM ABOUT THEIR SHEET
          CITES THEIR SHEET, A CLAIM ABOUT OUR SYSTEM CITES OUR DATABASE, AND
          THE SENTENCE MUST SAY WHICH. My first version of this comment called
          three of its figures "unconfirmable from our database" — LG's
          36-price ladder, NOYZ labour at $28, the hidden-tab count — and said
          they were not fine in writing. That collapses NOT DEMOABLE into
          UNBACKED, which is an over-correction: a reading of the client's
          workbook is properly sourced BY the client's workbook, and softening
          it would cost us the LG ladder, which is their best argument.
          NOT BACKED HERE ≠ NOT TRUE. What is worth flagging is narrower and
          real: we cannot put that ladder on a screen, because we hold six
          tier-1 rate rows for 250010 and no ladder at all. The page now says
          so in its own line rather than hedging. */}
      <SectionBox title="Brief for Scott — the eleven jobs in one page" layout="custom">
        <DocumentFrame
          slug="kb-doc/consultancy/outform/the-eleven-for-scott"
          label="The eleven jobs, four engines, the questions for Scott, and what one form must hold"
          width={900}
          height={DOC_H}
        />
      </SectionBox>
      <Break />

      <SectionBox title="Action items" layout="custom">
        <div style={{ marginTop: 0 }}>
          <TicketsKanbanPage project="outform" embedded />
        </div>
      </SectionBox>
      <Break />

      {/* ── 1-ter · THE COMPARABLES FLOW, SCREEN BY SCREEN ──────────────────
          Erez, 2 Sept, by voice: "Show the whole flow of the comparable jobs
          as we have it, as we will do it on one page — but bring as much as
          you can the logic, and show all the steps by showing screens of the
          whole flow. Don't touch the application, just create it on your
          board."

          It sits directly under Action items because it is the picture of the
          ticket immediately above it (MJOS-246 · Show comparable jobs), and a
          design nobody finds is a design nobody reviews.

          ⭐ THE SKIN IS JARVIS'S, DELIBERATELY. The primitives are copied from
          `command/outform-input-ux.tsx` — the same white cards on a coloured
          wash, the same hairline shadow, no card borders — so this reads as the
          same product as his input forms rather than a second design language.
          COPIED, not imported: his file is a design he owns and must not be
          edited from here, and a cross-board import would make this board's
          build depend on his. */}
      <SectionBox title="Comparable jobs — the whole flow, one page at nine states" layout="custom">
        <ComparablesFlow />
      </SectionBox>
      <Break />

      {/* ⛔ RETIRED 2 Sept — "The ballpark page, full size, with the
          comparables in it" stood here as a section of its own for about an
          hour. Erez: "you copied the wrong thing… that's the document I want
          you to copy and stay within the same section."

          NOTHING WAS LOST, which is the condition for taking anything off a
          board: the rendered page is the ImageBlock inside chapter 29, and the
          component that produced it — `BallparkWithComparables` — is still
          exported from `outform/comparables-flow.tsx`. Re-adding the live
          interactive version is one line if he wants it back. */}

      {/* ── 1-bis · EMAILS TO SEND ──────────────────────────────────────────
          `skill://email` says it plainly: "The board carries it in an **Emails
          to Send** section, numbered, newest last, as DocumentFrames. Additive
          — an email that has been sent stays on the board."

          I did not do that. I put the post-kickoff email into *Project docs* as
          the sixth frame of a three-column grid, which put the one ACTIONABLE
          thing on this board onto the second row of an archive section. Erez
          asked for "a text card I can copy" and then could not find it — which
          is the correct verdict on where I put it.

          **An action does not live inside a reference section.** Project docs
          holds the roadmap, the eleven topics and the meeting note: things you
          consult. An unsent email is a thing you DO, so it sits under Action
          items, where the eye already goes.

          Numbered and newest last. Nothing was removed from the board.

          ⚠️ AND THE SENTENCE ABOVE WAS TRUE WHEN WRITTEN AND STOPPED BEING
          TRUE. "It sits under Action items, where the eye already goes" was
          the intent; the board then grew to thirty sections and this one
          drifted to number 24, six from the bottom. Erez asked for the new
          email "under the action item" — which is this section's own stated
          rule, unmet. Moved to directly after Action items, 31 Aug.

          The general shape: a comment describing where something SITS is a
          claim about layout that nothing re-checks, and source order IS the
          layout here. Same family as a rule that depends on being remembered. */}
      <SectionBox title="Emails to send" layout="custom">
        <div style={{ marginTop: 0, display: "grid", gridTemplateColumns: `repeat(4, ${DOC_W}px)`, gap: 40, alignItems: "start" }}>
          <DocumentFrame
            slug="kb-doc/consultancy/outform/reply-email-draft"
            label="1 · reply on acceptance · four lines, plus the eleven answers as the call agenda · NOT SENT"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/kickoff/email-andy-zahid-systems"
            label="2 · Andy Hewison, system access · reply-all on the roadmap thread · NOT SENT"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/kickoff/email-post-kickoff-job-files"
            label="3 · after the kickoff, 18 Aug · the job files and the template range · NOT SENT"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/kickoff/email-01-part-identity"
            label="4 · 31 Aug · the database we built, the entity model, and four questions · NOT SENT"
            width={DOC_W}
            height={DOC_H}
          />
          {/* Email 02, drafted 2 Sept after seventeen analysis cycles. ONE job —
              what we still need to finish reading their data — and the other
              five findings parked visibly on the page rather than ridden along,
              per skill://email's "one email, one purpose". */}
          <DocumentFrame
            slug="kb-doc/consultancy/outform/kickoff/email-02-the-two-workbooks"
            label="EMAIL 02 · to Zaid — the Bottom Up for LG and NOYZ, and where Display Type comes from. 75 words, two asks, both forcing a reply"
            width={900}
            height={DOC_H}
          />
        </div>
      </SectionBox>
      <Break />

      {/* ── 1-ter · THE ATTACHMENT ──────────────────────────────────────────
          Erez, 31 Aug: "create the Excel and put it, like, as a file, like,
          next to the knowledge base page on the board with a download button
          so I can download Excel."

          It sits directly after Emails to send because it IS the attachment to
          email 4 — an action, not a reference, and the section above already
          learned what happens when an actionable thing drifts into an archive
          section.

          The file is NOT in this repo. It is on our R2 under
          pitch-doc/consultancy/outform/data-model/, same bucket and prefix as
          the client's own blueprint PDF, per the standing rule that assets go
          to R2 and never into a checkout every clone has to carry.

          ⚠️ The version is IN THE FILENAME on purpose. A client marks this up
          and sends it back, so "the workbook we sent" has to name exactly one
          artefact for ever. Overwriting v1 in place would make every earlier
          reference silently point at different content. New revision, new
          filename, new card. */}
      <SectionBox title="The attachment — data dictionary, sent with email 4" layout="custom">
        <div style={{ marginTop: 0, background: "#FFFFFF", border: "1px solid #E3E0D9",
                      borderRadius: 4, padding: 34, width: DOC_W * 2 + 40, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", color: "#8A8578" }}>
            XLSX · 8 tabs · 30 KB · v1 · 31 August 2026
          </div>
          <div style={{ fontSize: 30, fontWeight: 500, color: "#1C1C1C", margin: "10px 0 14px" }}>
            Outform — data model, field dictionary and open questions
          </div>
          <div style={{ fontSize: 17, lineHeight: 1.55, color: "#3A3A3A", maxWidth: 900 }}>
            Read me · 01 Entities (grain, key, source per table) · 02 Field dictionary (every field, 232 rows)
            · 03 Profiling (every measurement the questions rest on) · 04 Open questions (with our own answer
            first) · 05 Vendor mapping (24 strings against their D365 list) · 06 Labour rates (12 operations ×
            5 workbooks) · 07 Part identity (the evidence). Every tab carries a yellow
            <b> Outform answer</b> column for them to type into and send back.
          </div>
          <a
            href="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/outform/data-model/Outform-Data-Model-v1.xlsx"
            download
            style={{ display: "inline-block", marginTop: 24, padding: "14px 30px", background: "#1C1C1C",
                     color: "#FFFFFF", fontSize: 17, borderRadius: 3, textDecoration: "none" }}
          >
            Download the workbook
          </a>
          <div style={{ fontSize: 14, color: "#8A8578", marginTop: 16 }}>
            Also in <code>out/Outform-Data-Model-v1.xlsx</code> on the box.
          </div>
        </div>
      </SectionBox>
      <Break />

      {/* ── THE DATABASE, INTROSPECTED ──────────────────────────────────────
          Erez, 29 Aug: "how can we create an entity diagram from our database
          right now? But a detailed one that looks really good and put it on
          the board just like everything else."

          Generated from pg_catalog on the live Outform database — 51 tables,
          46 foreign keys — not drawn by hand. Regenerate with
          scratch/gen_erd.py + scratch/gen_erd_tsx.py.

          ⚠️ FLAT SVG ON PURPOSE. The board canvas is itself a ReactFlow;
          nesting a second one inside a section blanked this board earlier
          tonight. <rect>/<path>/<text> pans with the board and nests nothing.

          It sits directly under Action items because it is the one picture
          that makes every other section on this board legible: the two hubs
          are `jobs` (what the job is, 13 inbound FKs) and `raw.load_run`
          (where every number came from, 9 inbound). The second hub is the
          part of this system nobody else has. */}
      {/* ── THE EIGHT JOBS, ONE COLUMN EACH (Erez, 30 Aug) ─────────────────
          "I want to see eight pages for each of the eight jobs and then
          underneath each one of them the entity diagram and underneath that the
          tabs distribution diagram, in the same width of the knowledge base
          page. So I can see it very clean — each column is a job."

          THIS REPLACES NOTHING. The family sections below still hold the same
          jobs read family-by-family; this is the per-job read, which is the
          comparison that section cannot make. Same content, second axis.

          ⚠️ EVERY DIAGRAM IS SCALED TO THE COLUMN, NOT CROPPED. The generated
          SVGs are 1930-3390px wide and carry `width=`/`height=` attributes; a
          fixed-width parent alone would let them overflow and overlap the next
          column. `svg { width:100%; height:auto }` overrides both and the
          viewBox does the rest, so a 3390px diagram and a 1930px one land at
          exactly the same width — which is the whole point of the layout. */}
      <SectionBox title="The eight jobs — page, entity diagram, tab distribution" layout="custom">
        <div style={{ display: "flex", gap: JOB_COL_GAP, alignItems: "flex-start" }}>
          {JOB_COLUMNS.map((j) => (
            <JobColumn key={j.job} {...j} />
          ))}
        </div>
      </SectionBox>
      <Break />

      <SectionBox title="The database — 51 tables, introspected from pg_catalog" layout="custom">
        <OutformErd />
      </SectionBox>
      <Break />

      {/* ── 1-ante · WORKING ON NOW — THE LIVE BAND, DIRECTLY UNDER ACTION
          ITEMS ──────────────────────────────────────────────────────────────
          Erez, 28 Aug: everything in flight, in one row, at the top. Five
          frames, left to right in the order he named them, and each one is
          either something WE are making or something THEY just sent.

          It is a MOVE, not a copy. "The plan, end to end · internal", "Their
          demo · Data Pilot, end to end" and "Univer prototype" each had their
          own SectionBox further down this file; those boxes are gone and their
          reasoning came here with them, because a board that shows a thing
          twice is a board where nobody knows which copy is current. The
          sections that were NOT named — the data-architecture deck, the design
          versions, the file grid — stay exactly where they were.

          WHAT EACH FRAME IS, and why it is in a row about now:

          1 · THE INTERNAL PLAN. The deck Erez asked for after the client one
              missed: "It was supposed to be a presentation to us, to me, to
              you, to show the plan of what we're planning to do end to end."
              Ten slides, ~6½ min, narrated in af_sarah. What it carries that a
              client deck structurally cannot: the dependency order rather than
              phase names, the three-agent split, risk ranked by cost-if-true,
              and an explicit list of what we do not know. Slide 2 opens on "we
              have never loaded a single row". The client-facing data-
              architecture deck is parked intact further down and slide 7 makes
              it an artefact inside this one.

          2 · THEIR DEMO, DATA PILOT. The 8m53s demo Outform's CEO gave us,
              read frame by frame against the transcript — 98 blocks, 18 stills.
              Here because it is the competitor's answer to the same brief, and
              it now sits one frame away from Outform's own answer, which is
              the comparison that matters this week.

          3 · THEIR ESTIMATE TEMPLATE v4.1. Arrived 28 Aug and it RUNS — nine
              tabs, Internal/Client toggle, five governance roles, an editable
              rate panel, a production-mix slider. An iframe over the copy on
              our R2; see outform/template-v4.tsx for why a foreign 204 KB
              document cannot be inlined and why it is not in this repo.

          4 · THE CHANGE SUMMARY, v2 → v4. Their own change log, as a KB page
              with our reading beside it. ⭐ The reading is the point: sorted by
              what each feature rests on, every finished part of their prototype
              stands on real data and every unfinished part is labelled "Sample
              data" or "Awaiting actuals" — by them, in their own document.

          5 · UNIVER. Their workbook running in an editable open-source grid,
              with a real formula engine. The product question rather than the
              forensic one.

          ⚠️ The five widths are pinned arithmetic — see NOW_* above. They add
          to exactly 5980, which is the 6400 grid less one SectionBox padding.
          A sixth frame does not fit; something has to leave first. */}
      {/* ── PER JOB · 264714 (Erez, 29 Aug evening) ─────────────────────────
          "I just want to have, under the action items, a new section that has
          the diagram and a knowledge base page that explains it. I want to see
          sections like that, above Working on now." This is the first of them;
          one per job (or per template family) goes here as each is decoded.

          THE PAIR IS THE POINT: the picture and the words side by side. The
          diagram is board-NATIVE (`command/outform-264714-layers.tsx`, house
          engine) and the writing is a PRESENTED KB page.

          ⛔ AND THE DIAGRAM IS NOT INSIDE THE PRESENTED PAGE, DELIBERATELY.
          It was, for about an hour on 29 Aug, and it blanked this entire board:
          a board IS a React Flow canvas, so presenting a page carrying a
          FlowDiagram nests one canvas inside another. Measured — with the embed
          this board rendered 0 nodes and 625 chars while Falcon and Boiling were
          fine; without it, 826,729 chars. Jarvis confirmed the mechanism in
          source and wrote it into skill://board. The FlowDiagram block has been
          removed from that page; it must stay removed while the page is
          presented anywhere. */}
      {/* ── FAMILY 1 · THE CONVERTING ENGINE — five jobs, one system ────────
          Erez, 29 Aug: "do per family, but for each one you still need to do
          for each job a diagram and a knowledge base page."

          These five share one estimating system, proven by sheet signature
          rather than by eye: 243854, 244524, 255401 and 261152 are byte-identical
          in structure (`8698b493`, 26 sheets, ~41,000 formulas each), and 240266
          is the same lineage one generation earlier (`dd3a83de`, 23 sheets, no
          `Work` sheet, 4,168 formulas).

          ⭐ THE THING TO READ ACROSS THE ROW IS THE HIDDEN COUNT. L'Oréal and
          Sephora hide 25 of 26 — the estimator sees `Estimate` and nothing else
          while a 67,680-cell `Work` sheet with 35,736 formulas runs behind it.
          Lowe's and Winky Lux are the SAME FILE with `Work`, `Dashboard` and
          `Lookup` left visible. Same template, different visibility, decided per
          job and invisible to whoever quotes. */}
      <SectionBox title="Family 1 · the converting engine — 243854 · 244524 · 255401 · 261152 · 240266" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexWrap: "wrap", gap: NOW_GAP, alignItems: "flex-start" }}>
          <JobPair diagram={<Job261152Diagram />} slug="kb-doc/consultancy/outform/jobs/261152-loreal"
                   label="261152 · L'Oréal — 25 of 26 sheets hidden" />
          <JobPair diagram={<Job243854Diagram />} slug="kb-doc/consultancy/outform/jobs/243854-sephora"
                   label="243854 · Sephora Collection — the same engine, the same 25 hidden" />
          <JobPair diagram={<Job244524Diagram />} slug="kb-doc/consultancy/outform/jobs/244524-lowes"
                   label="244524 · Lowe's — same file, three sheets left visible" />
          <JobPair diagram={<Job255401Diagram />} slug="kb-doc/consultancy/outform/jobs/255401-winky-lux"
                   label="255401 · Winky Lux — same file, three sheets left visible" />
          <JobPair diagram={<Job240266Diagram />} slug="kb-doc/consultancy/outform/jobs/240266-merit"
                   label="240266 · Merit Beauty — the earlier generation, no Work sheet" />
        </div>
      </SectionBox>
      <Break />

      {/* ── THE FIVE ENGINES, WIRED ─────────────────────────────────────────
          Erez, 29 Aug: "let's add the entity diagram to each job analysis…
          dive really deep into each one and make them 100% correct."

          ⚠️ THESE ARE ENTITY DIAGRAMS IN THE STRICT SENSE, not a metaphor. In
          a database the declared edges are foreign keys. In a workbook they are
          CROSS-SHEET FORMULA REFERENCES — equally declared, equally machine-
          readable, directional. Every edge here was extracted from a formula.

          ROLE IS DERIVED FROM TOPOLOGY, NOBODY ASSIGNED IT. Out-edges and no
          in-edges = a REPORT (nothing depends on it). No out-edges = an INPUT.
          Both = the ENGINE. Neither, while carrying content = ISOLATED, which
          is how `Bottom Up- sales (2)` convicts itself.

          ⚠️ HOW WE KNOW THEY ARE COMPLETE. The extractor counts every `!`
          reference in every formula and resolves each one; whatever it cannot
          name is REPORTED, never dropped. Across all 29 distinct workbooks:
          0 unresolved. That audit caught a real bug — the first regex let a
          bare sheet name swallow the function in front of it, so
          `TRIM(Work_BOM!A1` parsed as a sheet named `TRIM(Work_BOM` and 10,759
          of LG's 15,555 references silently resolved to nothing. A sparse
          diagram and a correct one look identical; only the audit told them
          apart, and LG's real edge count is 28 rather than 24.

          ⚠️ FIVE, NOT ELEVEN, AND THAT IS THE FINDING. 243854 · 244524 ·
          255401 · 261152 return the SAME 26 sheets, 41 edges and 3,365
          references — one engine, four jobs. Eleven diagrams would be the same
          picture four times. Regenerate: scratch/gen_sheetgraph_tsx.py

          ⚠️ ONE SECTION, NOT FIVE, DELIBERATELY. A single DocumentFrame blanked
          this board earlier tonight. These are board-native SVG and fetch
          nothing, but the honest move after that is one reversible insertion
          Erez can look at, not five scattered through someone else's sections. */}
      <SectionBox title="The five engines, wired — every edge extracted from a formula, 0 unresolved" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexWrap: "wrap", gap: NOW_GAP, alignItems: "flex-start" }}>
          <SheetGraph_converting />
          <SheetGraph_merit />
          <SheetGraph_lg />
          <SheetGraph_clarity />
          <SheetGraph_morphe />
        </div>
      </SectionBox>
      <Break />

      <SectionBox title="264714 · Morphe — every tab, and the layer it became" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: NOW_GAP, alignItems: "flex-start" }}>
          <Outform264714LayersDiagram />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/the-workbook-mapped"
            label="264714, the workbook mapped — all 19 tabs, what we took, where it landed, and what is still missing"
            width={NOW_HAND_W}
            height={NOW_H}
          />
        </div>
      </SectionBox>
      <Break />
      {/* ── FAMILY 3 · LG'S OWN SYSTEM — one job, and the heaviest file we hold */}
      <SectionBox title="Family 3 · LG's own system — 250010" layout="custom">
        <div style={{ marginTop: 0 }}>
          <JobPair diagram={<Job250010Diagram />} slug="kb-doc/consultancy/outform/jobs/250010-lg"
                   label="250010 · LG Electronics — 52,593 formulas, a visible Settings tab, and 36 prices at once" />
        </div>
      </SectionBox>
      <Break />

      {/* ── FAMILY 4 · THE CLARITY TOOL — the new generation, alongside Morphe */}
      <SectionBox title="Family 4 · the Clarity Tool — 261801" layout="custom">
        <div style={{ marginTop: 0 }}>
          <JobPair diagram={<Job261801Diagram />} slug="kb-doc/consultancy/outform/jobs/261801-noyz"
                   label="261801 · NOYZ — 10 sheets, one hidden, four kit BOMs. The generation Outform is moving to" />
        </div>
      </SectionBox>
      <Break />

      {/* ── THE THREE THAT CANNOT BE INGESTED AS THEY STAND ──────────────────
          Kept as a section rather than a footnote, because "we have eleven jobs"
          is only true if these three are visible. 264511 is a Ballpark template
          — 62 formulas and 6 MB of images, a costing REQUEST rather than an
          estimate. 253842 and 264013 have no spreadsheet in the folder at all:
          two drawings and five PDFs respectively. Nothing here is blocked on
          engineering; all three are blocked on Outform telling us what the
          estimate of record for them is, or that there isn't one. */}
      <SectionBox title="Not ingestable as they stand — 264511 · 253842 · 264013" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexWrap: "wrap", gap: NOW_GAP, alignItems: "flex-start" }}>
          <JobPair diagram={<Job264511Diagram />} slug="kb-doc/consultancy/outform/jobs/264511-dolce-gabbana"
                   label="264511 · Dolce & Gabbana — a Ballpark template, not an estimate" />
          <JobPair diagram={<Job253842Diagram />} slug="kb-doc/consultancy/outform/jobs/253842-orphan"
                   label="253842 · the orphan drawing set — no spreadsheet, no client attribution" />
          <JobPair diagram={<Job264013Diagram />} slug="kb-doc/consultancy/outform/jobs/264013-tcl"
                   label="264013 · TCL North America — five PDFs and no workbook" />
        </div>
      </SectionBox>
      <Break />
      <SectionBox title="Working on now" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: NOW_GAP, alignItems: "flex-start" }}>
          {/* Erez, 29 Aug, after opening 264714 and 261801 himself: "I need to
              understand these Excel sheets as good as possible ... did they
              create it for every job? Do they have templates they keep
              copying?" Measured across all 29 workbooks, and the answer moved
              the project: 13 lineages, three generations of engine, and the two
              he picked are the NEW generation — visible, six functions, and
              their entire backend is ~30 rows of reference data. Leads the row
              because it is what we are working on now; the handover doc keeps
              its place immediately to the right. */}
          {/* Erez, 29 Aug: "a document that explains every job that they gave
              us ... a couple paragraphs about each job ... and at the top an
              executive summary." Eleven jobs, not twelve — the twelfth folder
              is _UNFILED, two Winky Lux drawings whose filenames carry no job
              number. Leads the row because it is the map of the whole corpus;
              everything else on this board is a slice of it. */}
          {/* Erez, 16:20: "I feel like we're just running in cycles, but we
              don't have a good way to validate... I need some best practices
              here and we need to follow some methodology that Atlas can prove
              he's doing it correctly, cycle by cycle." This is that: five gates
              per job, each a number rather than an opinion, with every target
              figure taken from Outform's own workbook. Leads the row because
              until this exists, nothing else on this board can be called done. */}
          {/* ⛔ REMOVED 29 Aug 19:5x — `kb-doc/consultancy/outform/the-workbook-mapped`
              was presented here and the board went blank. That page contains a
              FlowDiagram, so presenting it put a ReactFlow INSIDE the board's own
              ReactFlow canvas — and this board already carries 26 DocumentFrames,
              each fetching and rendering a whole document.
              The page itself is fine and is live in the KB; it is the PRESENTATION
              of a diagram-bearing page on a diagram-based canvas that is not safe.
              Read it at /kb-doc/consultancy/outform/the-workbook-mapped. */}
          <DocumentFrame
            slug="kb-doc/consultancy/outform/how-we-know-it-is-right"
            label="How we know it is right — five gates per job, and the scorecard that makes them visible"
            width={NOW_HAND_W}
            height={NOW_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/the-eleven-jobs"
            label="The eleven jobs — every job they sent us, what it cost, and what is missing from it"
            width={NOW_HAND_W}
            height={NOW_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/the-engine-in-the-excels"
            label="The engine in the Excels — 13 lineages, 3 generations, and the ~30 rows that are the whole backend"
            width={NOW_HAND_W}
            height={NOW_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/delivering-to-an-enterprise"
            label="How you deliver this to a CIO and a Chief Estimator — the seven artefacts, and the acceptance moment"
            width={NOW_HAND_W}
            height={NOW_H}
          />
          <DeckFrame
            slug="pitch-doc/outform/internal-plan"
            label="The plan, end to end · internal — 10 slides, ~6½ min, for Erez, Jarvis and Atlas"
            width={NOW_DECK_W}
            height={NOW_DECK_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/competitor-demo"
            label="Their demo · Data Pilot — the 8m53s demo, 53 frames against the transcript"
            width={NOW_DEMO_W}
            height={NOW_H}
          />
          <OutformTemplateV4 width={NOW_TMPL_W} height={NOW_H} />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/estimate-template-v4"
            label="Their change summary v2 → v4 — what they built, and what is real in it"
            width={NOW_SUMM_W}
            height={NOW_H}
          />
          <div>
            <Suspense fallback={<div style={{ width: NOW_UNIV_W, height: NOW_H }} />}>
              <OutformUniverSheet width={NOW_UNIV_W} height={NOW_H} />
            </Suspense>
          </div>
        </div>
      </SectionBox>

      {/* ── 1-ante-0 · THE PLAN, NARRATED ───────────────────────────────────
          Erez, 28 Aug: "create a pitch deck explaining everything you think
          you want to do and all the other things… and then add an action item
          under action item, a pitch deck that I can play."

          Both halves done: this is the deck, and OUT-04 "Play the deck" is the
          card for it in the kanban above, so it is a thing to DO rather than a
          thing to notice. Ten slides, 6.6 minutes, narrated in af_sarah on this
          box's own Kokoro — press play and it advances itself.

          ⚠️ ONE CLIP HAD TO BE REGENERATED AND THE CHECK THAT CAUGHT IT IS THE
          PART WORTH KEEPING. Slide 3's mp3 was written while a shell timeout
          killed ffmpeg mid-encode, leaving a 126 KB file where 513 KB was due —
          and my resume logic skipped it, because it tested `size > 5000`, which
          a truncated file passes. Existence is not a check. What caught it was
          measuring SPEECH RATE: every good clip sits at 16.7–18.1 characters
          per second and the broken one read 70.7, because the text was full
          length and the audio was ten seconds. A deck whose third slide cuts
          off mid-sentence would otherwise have gone straight to Erez.

          What it argues, in order: what their prototype proved · their 27
          arrays sorted into four groups · the eleven split 5/4/2 · the six-stage
          pipeline · the framework question answered · where the sizing is NOT
          certain · what we do Monday. Slide 9 is the one to keep honest — it
          exists because he asked "are you 100% sure", and the answer is no. */}
      <SectionBox title="How we build it · narrated" layout="custom">
        <div style={{ marginTop: 0 }}>
          <DeckFrame
            slug="pitch-doc/outform/how-we-build-it"
            label="10 slides · 6.6 min · press play, it narrates itself — ends on three decisions for Erez"
            width={2300}
            height={1360}
          />
        </div>
      </SectionBox>
      <Break />

      {/* ── 1-ante-a · THE PROCESS, DRAWN ──────────────────────────────────
          Erez, 28 Aug: "can you show me what it looks like, the whole process?
          Create like a diagram and explain it, and put it under the action
          items."

          TWO diagrams, and the first one is NOT MINE. Atlas built
          `command/data-programme.tsx` this morning — six stages, one human
          step, the loop — and it is already mounted on the R&D board. I am
          rendering the SAME COMPONENT here rather than drawing a second
          version of it: one component shown on two boards cannot drift, where
          two drawings of one pipeline certainly would. If it changes, it
          changes in both places, which is the only reason this is allowed.

          The second is mine and it exists because his answers a different
          question. His: what happens to a client's data, end to end. Mine:
          the pipeline publishes a corpus — so what comes OUT of it, and which
          of it is blocked? `skill://diagrams` is explicit that the archetype
          is chosen by the reader's question, and these are two questions.

          ⭐ WHAT MINE SAYS THAT NOTHING ELSE ON THIS BOARD DOES: the eleven
          derivations their own front end needs are not one lump. Grouped by
          BLOCKER they split 5 / 4 / 2 — five are derivable from the 51 files
          already in our hands, four wait on quote outcomes, two wait on the
          loss debriefs. And the two red sources are precisely the two their
          prototype had to fabricate. They did not fake the cost history,
          because the cost history exists. */}
      <SectionBox title="The process, end to end" layout="custom">
        <div style={{ marginTop: 0 }}>
          <DataProgrammeDiagram />
        </div>
      </SectionBox>

      <SectionBox title="The eleven, and what blocks each" layout="custom">
        <div style={{ marginTop: 0 }}>
          <OutformElevenDiagram />
        </div>
      </SectionBox>
      <Break />

      {/* ── THE WORKBOOK READ AS SOFTWARE ──────────────────────────────────
          These two answer the question Erez put on 29 Aug and nobody had
          measured: the ingest read what the cells SAY, not what they DO.
          333,657 formulas across 29 workbooks — 52% of every non-empty cell.
          The first diagram is the anatomy of one file and what is broken in
          it; the second is whether there is one engine here or seven. They are
          deliberately two sections and not one: "what is inside this thing"
          and "how many of them are there" are different questions, and
          skill://diagrams says choose by the question the reader is asking. */}
      <SectionBox title="The workbook is an application" layout="custom">
        <div style={{ marginTop: 0 }}>
          <OutformWorkbookAnatomy />
        </div>
      </SectionBox>

      <SectionBox title="Seven jobs, one program" layout="custom">
        <div style={{ marginTop: 0 }}>
          <OutformCloneDrift />
        </div>
      </SectionBox>

      {/* The written half of the same work. The two diagrams above show the
          SHAPE of the engine; this is what it DOES — the six layers, the
          caliper x area rate schedules that are the actual moat, the traced
          #REF! chain that killed the margin calculator, and the mismatch
          between the converting-shop engine they have and the global-sourcing
          front end they drew. Presented, not retyped: it lives as a KB page,
          which is the board law (root CLAUDE.md, "WHAT A BOARD IS MADE OF"). */}
      {/* Erez, 29 Aug: "I'm behind in my understanding of the data process ...
          I don't know what I don't see." This is the primer, and it goes FIRST
          of the two because it is the simple one — the software lifecycle he
          already has, mapped onto the data one, with every term defined by
          pointing at Outform's own files rather than at a textbook. The
          Excel-backend page below is the detail you read second. */}
      <SectionBox title="The data project, explained" layout="custom">
        <DocumentFrame
          slug="kb-doc/consultancy/outform/data-project-primer"
          label="Primer · the lifecycle, the vocabulary, what governance means, and the blind spots"
          width={2600}
          height={1500}
        />
      </SectionBox>

      <SectionBox title="The Excel backend, explained" layout="custom">
        <DocumentFrame
          slug="kb-doc/consultancy/outform/the-excel-backend"
          label="Six layers, the rate schedules, what is broken, and the front-end mismatch"
          width={2600}
          height={1500}
        />
      </SectionBox>
      <Break />

      {/* ── 1-ante-bis · THEIR PROTOTYPE, READ AS A SPECIFICATION ───────────
          Erez, 28 Aug, after looking at the band above: "they gave us the front
          end how they see it's supposed to look. That should be a big indicator
          also for the data migration."

          So this is the analysis of their SOURCE rather than their surface —
          both data scripts extracted, stubbed and RUN, so every count on the
          page came from a command instead of a reading. What it establishes:

          · The 351 closed quotes their banner calls real are seven hardcoded
            rows summing to exactly 351, and the file makes no network call of
            any kind. Their own developer comment says the figures are
            illustrative while their on-page banner says the dataset is real —
            both sentences are in the same file. That closes the question I
            left open this morning, and closes it the other way.
          · 27 arrays, 214 rows, 47 BOM lines, $462.15 fully loaded unit cost,
            and a benchmark on only 7 of the 14 categories.
          · Sorted by where each array would REALLY have to come from, eleven
            can only come from normalized history — which is the corpus
            project, itemised, in their handwriting.

          It sits directly under the band because it is the argument the band
          exists to support. Its own row: 2300 boxes out to 2720 of the 6400
          grid, the same footprint as the data-architecture deck further down.
          The <Break /> below (which belongs to the prototypes) is what keeps
          it there, so do not remove that one without adding one here. */}
      <SectionBox title="Their prototype, read as a spec" layout="custom">
        <div style={{ marginTop: 0 }}>
          <DocumentFrame
            slug="kb-doc/consultancy/outform/prototype-as-spec"
            label="27 arrays, 214 rows, no network calls — and what that says about the normalization"
            width={2300}
            height={1700}
          />
        </div>
      </SectionBox>

      {/* ── 1a-bis · THE TWO KEPT PROTOTYPES, EACH ITS OWN SECTION, RIGHT
          UNDER ACTION ITEMS ────────────────────────────────────────────────
          Three shapes in one session (tabs → six shrunk thumbnails in one
          row → six full-size sections), then — having finally seen all six
          full size, side by side — Erez reviewed them and cut four. Full
          reasoning + what to remember from the deleted four lives in
          design-versions.tsx, next to the array this maps.

          Erez, 23 Aug: "let's clean everything and keep just Story 1, Story
          2 — make Story 1 on the left and Story 2 on the right, delete the
          other ones."

          OUTFORM_DESIGN_VERSIONS now holds exactly those two, Story 1 first
          — this map renders them left to right in array order, so array
          order IS his "left / right" instruction. Each still renders at
          SCREEN_W, the Action-items standard. <Break /> still forces them
          onto a fresh row below Action items; at two sections they sit on
          ONE row (SCREEN_W × 2 + padding + gap is well under the 6400-wide
          grid), which is also what "side by side" asks for. */}
      <Break />
      {OUTFORM_DESIGN_VERSIONS.map((v) => (
        <SectionBox key={v.key} title={`Product prototype · ${v.n}`} layout="custom">
          <div style={{ marginTop: 0 }}>
            <Suspense fallback={<div style={{ width: SCREEN_W, height: 1400 }} />}>
              <OutformDesignVersionScreen versionKey={v.key} width={SCREEN_W} />
            </Suspense>
          </div>
        </SectionBox>
      ))}

      {/* ── 1a-ter · THE FILES THEY SENT US ────────────────────────────────
          Erez, 26 Aug: "I want to see all the files they gave us previewed so
          I can actually see what's in there... group them together based on
          jobs... I want to see it like a tile of files previews."

          51 files, 12 jobs, one tile each, previewed rather than listed. What
          a tile actually draws — the real PDF vs our redraw of a workbook —
          and why a workbook cannot be photographed on this box, is in
          outform/files.tsx. Data is one JSON on R2 with the 51 originals
          beside it; NOTHING of the client's is in this repo, because
          `public/` is copied into the Pages deploy verbatim and that is
          exactly how one client's config reached another client's domain on
          20 Aug.

          ⚠️ PLACED HERE, NOT BESIDE THE PROTOTYPES, AND THE NUMBER DECIDED IT.
          He asked for it "in the second row next to the prototypes". Two
          prototypes box out at 2 x (1710 + 420) + 170 = 4430 of the 6400
          grid, leaving 1800 — a content width of 1380, which is four tiles
          across and thirteen rows DOWN for 51 files. That is the tall column
          the board law exists to prevent. Full width takes the same 51 files
          to sixteen across and four rows, so it sits directly BELOW the
          prototypes on its own line: same band, right where he is looking,
          and wider than tall. */}
      <SectionBox title="The files they sent us" layout="custom">
        <div style={{ marginTop: 0 }}>
          <OutformFileGrid width={5980} />
        </div>
      </SectionBox>
      <Break />

      {/* ── 1a-quater · DESIGN INSPIRATION — shapes.co ──────────────────────
          Erez, 27 Aug: "I want us to take design inspiration from this
          website… capture all the images that show how the app looks… then
          let's talk about this design pattern and learn what it is."

          92 product images off all 20 pages, grouped by TREATMENT rather than
          by page, because the three treatments are the finding. How they were
          selected — and why by eye rather than by a size threshold — is in
          outform/shapes.tsx.

          Sits below the file grid on its own full-width row: 92 tiles at three
          across the 1380 a side-by-side slot allows would be thirty rows down,
          which is the tall column the board law exists to prevent. */}
      <SectionBox title="Design inspiration · shapes.co" layout="custom">
        <div style={{ marginTop: 0 }}>
          <ShapesInspiration width={5980} />
        </div>
      </SectionBox>
      <Break />

      {/* ── 1a-quinquies · THE COMPETITOR DEMO, READ END TO END ────────────
          Erez, 27 Aug: analyse the video their CEO gave us and put "screen
          captures of everything, and right next to them, with as much
          detail, explaining the whole end-to-end video and story."

          Per board law a section IS a KB page presented — so the analysis is
          a page_content row (98 blocks, 18 frames hosted on R2) rendered
          here with <DocumentFrame>. That keeps the writing editable with
          update_kb_page and off this file entirely, and it is why the
          screen captures and the prose sit next to each other: they are the
          same document.

          ⚠️ THE PAGE WAS WRITTEN WRONG THE FIRST TIME AND IT WOULD HAVE
          SHIPPED SILENTLY. BlockRenderer's schema is { type, props: {...} };
          the first write used FLAT blocks ({ type, title, body }), so all 98
          rendered as perfectly-styled EMPTY SHELLS — right structure, zero
          text. No error, no warning: the renderer only warns on an unknown
          TYPE, and every type was valid. Caught by server-rendering the
          stored row through the real architectureConfig before wiring it
          here (21,578 bytes / 0 of 9 text probes, against 90,975 bytes /
          9 of 9 once nested). BlockRenderer.tsx's own header records the
          same class of failure hiding 47 blocks across 11 pages on 15 Aug.
          Render the row, do not trust the write.

          Two frames wide because the document is dense with tables and
          screenshots; taller than the usual 1180 for the same reason. */}
      {/* ── 1b · THE DATA ARCHITECTURE, AS A NARRATED DECK ─────────────────
          Erez, 28 Aug: build the plan as a pitch deck "as if you are Data
          Pilot… as if you are Tailormind's data division", playable with
          voice.

          Per skill://pitch-deck this is a page_content row at
          pitch-doc/outform/data-architecture rendered with pitchDeckConfig —
          no bespoke per-deck TSX — and <DeckFrame> is the board surface that
          brings the Deck block's own play button, arrows and dot indicators
          inline on the canvas.

          NARRATION IS MANDATORY for a deck (Erez-locked 2026-07-25) and it is
          Quinn's own af_sarah: 12 clips, 6.4 minutes, generated on this box's
          Kokoro at :8140 and transcoded to mp3, because the Deck block loads
          slide-NN.mp3 and the service emits ogg. All 12 verified public and
          serving as audio/mpeg. `audioBase` is what makes the play button
          appear at all.

          ⚠️ EVERY NUMBER ON THESE SLIDES WAS MEASURED on the 51 files, not
          asserted — the classification, the Heaps'-law curve, Copack/COPAK,
          the error counts. The one extrapolation (2,000 folders) says on its
          own slide that it rests on 8 client-selected jobs. This deck is
          written to be shown to Outform, so a number we cannot defend is
          worse here than a number we do not have. */}
      {/* ── 1b-bis · THE INTERNAL PLAN MOVED UP, 28 Aug ────────────────────
          It was a full-width section here. It is now the first frame of
          "Working on now" directly under Action items, at Erez's instruction,
          and its reasoning travelled with it — read the comment on that
          section. The client-facing deck below stays exactly where it was: he
          named the internal plan and did not name this one, and slide 7 of the
          internal plan is what makes this one an artefact inside it. */}
      <SectionBox title="The data architecture · narrated" layout="custom">
        <div style={{ marginTop: 0 }}>
          <DeckFrame
            slug="pitch-doc/outform/data-architecture"
            label="12 slides · 6½ minutes · press play, it narrates itself"
            width={2300}
            height={1360}
          />
        </div>
      </SectionBox>
      <Break />

      {/* ── THE DATA PILOT READ-THROUGH MOVED UP, 28 Aug ───────────────────
          Same move as the internal plan: it is now the second frame of
          "Working on now". The document itself is untouched — 98 blocks and 18
          stills at kb-doc/consultancy/outform/competitor-demo — and the note
          above about how it was written wrong the first time (flat blocks, 98
          perfectly-styled empty shells) stays with the section that renders it
          so the next person writing a KB page reads it before they need it. */}

      {/* ── 2 · PROJECT DOCS — the same four-document shelf Otto built for
          Falcon, in Erez's order: where the engagement stands · what the
          product must do · the contract every client box is built against ·
          this instance.

          His reasoning on the Falcon board holds identically here: the
          architecture already has TWO canonical documents — Tailormind Core and
          Tailormind Outform — so anything true about this box belongs in one of
          those, never in an Outform-local retelling that drifts from them. The
          board points at the source instead of paraphrasing it.

          The project summary is <ProjectFrame>, which reads the `projects` ROW
          directly through the same mdToBlocks converter the /projects/<slug>
          detail page uses. **Nothing here can drift, because nothing here is a
          copy.**

          I got this wrong first: I published the row's content as a KB page at
          kb-doc/projects/outform and pointed a DocumentFrame at it. Erez had
          decided the same afternoon that the row is the single source of truth
          and the parallel kb-doc/projects/* pages go — every one of them had
          been frozen since 25 July while the rows stayed current, so Falcon's
          page still said "nothing deployed" six weeks after go-live. A copy of
          a living row is a stale page waiting to happen, and I had just made
          one. */}
      <SectionBox title="Project docs" layout="custom">
        {/* ⚠️ WRAPS AT THREE, AND THE NUMBER IS MEASURED not guessed. Jarvis
            measured this box at 4014 flow units against a 3508 budget — over by
            506 — which is why "Project docs | Univer prototype" wrapped instead
            of pairing. Four 880-wide frames plus three 24 gaps is 3592, and
            SectionBox adds 210 a side: 4012, within two units of his reading.
            At three across it is 2688 + 420 = 3108, inside the budget with 400
            to spare, and the fourth frame drops to a second line INSIDE this
            box rather than costing the board a whole row.
            ⚠️ He also measured Action items at 2132 — comfortably fine. I had
            named both as suspects; only one was. Do not shrink the kanban. */}
        <div style={{ marginTop: 0, display: "flex", flexWrap: "wrap", gap: 24, maxWidth: 3 * DOC_W + 2 * 24 }}>
          <ProjectFrame
            slug="outform"
            label="Project summary — where the engagement stands"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/brd"
            label="BRD — their thirteen sections as the spine, and our answer to each"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/myjarvis-os/architecture/tailormind-core"
            label="Tailormind Core — the contract every client box is built against"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/myjarvis-os/architecture/tailormind-outform"
            label="Tailormind Outform — this instance, its divergences and open questions"
            width={DOC_W}
            height={DOC_H}
          />
        </div>
      </SectionBox>

      {/* ── THEIR WORKBOOK, LIVE AND EDITABLE — 20 AUGUST 2026 ───────────────
          Erez: "I don't want it to be a drawing. I want it to be editable, like
          they can use it. Can you just show me an example of this Univer with
          their data?"

          This is Univer 0.25.1 (Apache-2.0) actually running, loaded with the
          cells Zahid sent on 18 August. Editable, with a working formula engine
          — `@univerjs/engine-formula` and `@univerjs/sheets-formula` are in the
          open-source core, which is the whole reason Univer answers "editable,
          Excel-grade, open source, completely in our control".

          WHY IT IS THE RIGHT PICK, measured rather than argued: all 41,342
          formulas in their file use exactly TWELVE distinct Excel functions —
          IF, NOT, IFERROR, LOOKUP, OR, SUM, AND, ROW, TRIM, FIND, LEN, RIGHT.
          Engine coverage was never the risk. Import FIDELITY is, and that is
          our code either way: Univer's own docs prescribe parsing the file with
          any open-source library and handing it an IWorkbookData snapshot. Only
          their hosted conversion SERVER is Pro — and we would not want it, since
          the agent must produce files server-side in our own control anyway.

          ⚠️ THIS DEMO MAPS VALUES AND FORMULAS ONLY. No merged cells, column
          widths, number formats or styles. Anything missing is missing because
          nothing mapped it, not because Univer cannot hold it — the round-trip
          test is what will tell us which.

          The section above stays: it is the READ-ONLY forensic view, where the
          point is the 3,516 errors and the 25 hidden sheets. This one is the
          product question. Two questions, two surfaces. */}
      {/* ── THEIR WORKBOOK, LIVE AND EDITABLE ───────────────────────────────
          Univer 0.25.1 (Apache-2.0) running with the cells Zahid sent on 18
          August. Editable, real formula engine — the whole reason Univer
          answers "editable, Excel-grade, open source, completely in our
          control". All 41,342 formulas in their file use exactly TWELVE Excel
          functions, so engine coverage was never the risk; import fidelity is,
          and that is our code either way.

          ⚠️ REMOUNTED 20 Aug after it took this whole page down once. Three
          things changed and all three are the point:
            · every Univer call is inside a try — the first version guarded only
              createUnit, so `new Univer()` and eleven registerPlugin calls threw
              straight into React;
            · the component carries its OWN error boundary, deliberately not
              ChunkErrorBoundary, which RELOADS the page — the wrong blast radius
              for one section of a board;
            · it fetches through useApi(), because /api/xl-sheets answers 401 to
              a bare fetch, which is why it rendered empty before that.

          ⚠️ Values and formulas only — no merged cells, widths, number formats
          or styles, and edits are local because the API is read-only by design. */}
      {/* ── UNIVER MOVED UP, 28 Aug ────────────────────────────────────────
          Now the fifth frame of "Working on now" — Erez asked for it "close to
          us" in the same row. Everything above still applies to it there,
          including the three remount fixes and the read-only API.
          ⚠️ Project docs used to PAIR with this box on one row (3108 + 170 +
          2000 = 5278 of the 6400 grid). With Univer gone it sits alone unless
          something of similar width follows it. That is the wrap changing, not
          a section going missing. */}

      {/* ── 1a · THEIR BLUEPRINT, AND WHAT WE TOOK FROM EACH PART ───────────
          One card per page, drawn to the same box as the page, answering three
          questions and nothing else. All 24 pages are here including the unread
          ones, because a map that lists only what you have done cannot tell you
          what is left.

          ── THE MARKER LIVES HERE NOW (Erez, 18 Aug) ──────────────────────────
          "Remove this tool that we created where we can highlight the things —
           although it's a really cool tool, just add the functionality of it to
           their blueprint, what we took from each part, so I can click the pages
           that are just open in front of me."

          There WAS a second section holding another copy of the same 24 pages
          purely to carry the marker. Two surfaces showing one document is one
          too many, and the marks belong on the page you are reading the summary
          against. So: click a paragraph on any still and it goes yellow, click
          again and it does not. One gesture — two earlier ones (drag-to-select,
          double-click-a-word) were deleted long ago for the same reason.

          ⭐ AND IT COSTS NO CANVAS. `blocksForDoc` reads the PDF for TEXT
          GEOMETRY ONLY — `getTextContent()` at scale 1, in percent — while the
          R2 stills carry the picture. Rasterising is what put 367 MB of bitmap
          on this board and made it go blank past 2.5× zoom; the feature gets
          richer and the thing that broke the board is simply absent.

          Marks are rows in `doc_highlights`, not browser state, so the agent
          walking the document can read what a human has actually settled. */}
      <SectionBox title="Blueprint Analysis" layout="custom">
        {/* The component sets its own width from PER_ROW — three pairs, 5,374px.
            Do not pin a width here as well; two numbers for one layout is the
            drift this board has already paid for once. */}
        <div style={{ marginTop: 0 }}>
          <OutformCoverage />
        </div>
      </SectionBox>
      <Break />


      {/* ── 1a-ter · THEIR ESTIMATING WORKBOOK, LIVE ────────────────────────
          Zahid Randhawa (Head of Estimating) sent three .xlsx files on 18 Aug
          2026 with the note that clients "often request this level of detail".
          The Bottom-Up file IS the Excel our architecture page had listed for a
          week as "named as the core of detailed costing — we have never seen it."

          Erez: "Is it possible to show them as Excel files on the board, that I
          can actually look at them and touch them?"

          ⚠️ A DOCTRINE NOTE, RAISED NOT ASSUMED. The board law names three
          components — a KB page presented, a presentation, a diagram — and a
          live spreadsheet grid is none of them. I have built it as "the client's
          document, presented", which is the closest true reading, and the
          analysis of it lives where the law says it should: a KB page, beside
          the file it is about. If Erez reads it as a fourth component the grid
          comes off the board, not the finding.

          WHY IT IS A GRID AND NOT A PICTURE: 41,140 formulas, 3,516 of which
          evaluate to an error, and 25 of the 26 sheets hidden from the person
          doing the estimating. A screenshot shows none of that. Click a cell
          and its formula is in the bar; the tab strip carries the hidden sheets
          with their error counts on them. Rows are in `xl_sheets` (sql/055) via
          /api/xl-sheets, read-only — a write path would let our own UI edit the
          evidence a client sent us. */}
      <SectionBox title="Their estimating workbook" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: 40, alignItems: "flex-start" }}>
          <OutformWorkbooks width={2600} height={1500} />
          <DocumentFrame slug="kb-doc/consultancy/outform/their-workbook" label="What the workbook says" width={1180} height={1500} />
        </div>
      </SectionBox>

      {/* "The product — prototype" folded INTO the switcher above, 22 Aug —
          it rendered `OutformWorkbench`, which is now version v0 there, the
          first attempt. Its content moved rather than went: one click, same
          component, same file. Keeping a second box for the oldest version of
          the thing the box above already holds is the duplication this
          consolidation exists to remove. */}

      {/* ── 1b · THE DOCUMENT WE ARE BUILDING, AS ELEVEN PAGES ──────────────
          These used to sit beside a second copy of their PDF in a section called
          "Mark up their blueprint". Erez, 18 Aug: "remove this tool that we
          created where we can highlight the things — although it's a really cool
          tool, just add the functionality of it to their blueprint, what we took
          from each part."

          He is right: TWO SURFACES SHOWING THE SAME 24 PAGES was one too many,
          and the marks belong on the page you are reading the summary against,
          not on a separate copy of it. The marker moved into the coverage
          section above and this section keeps the eleven sheets, which were only
          ever here because they shared a box with the PDF.

          ⚠ FIVE PER ROW, AND THE NUMBER IS A HARD BROWSER LIMIT, NOT TASTE.
          Chrome will not paint a composited layer past ~16,384px and
          `.react-flow__viewport` IS that layer, scaled by the board zoom — so
          anything further right than 16,384 / zoom is simply not drawn. At
          MAX_ZOOM 2.5 the usable width is 6,554px, and eleven frames on one row
          reach 9,410px. Zooming in to read made the last four sheets vanish with
          no error and nothing in the console, and they came back on zooming out,
          which is exactly why it read as a mystery for an hour. */}
      <SectionBox title="The document we are building" layout="custom">
        <div style={{ marginTop: 0, display: "grid", gridTemplateColumns: `repeat(5, ${DOC_W}px)`, gap: 40, alignItems: "start" }}>
          {BUILD_PAGES.map((p) => (
            <DocumentFrame key={p.slug} slug={p.slug} label={p.label} width={DOC_W} height={DOC_H} />
          ))}
        </div>
      </SectionBox>

      {/* ── 1a · COVERAGE — their document, page by page, and what came out of
          each part. Erez, 17 Aug, and it is a rejection of what we had:

            "This is not working for me. It's just not… I want to map each
             section from their blueprint into a box and the box needs to
             explain exactly what did we learn from this section. Did we add
             something? Did we create a question?… I want to see PDF page, a
             section, and then an arrow from that section into a box, and in the
             box everything that we extracted."

          The failure it replaces is not that the extraction was wrong — it was
          right, and spread across ten documents, so the only way to know whether
          a section was finished was to read all ten and hold the answer in your
          head. A RECORD YOU CANNOT AUDIT AT A GLANCE IS ONE NOBODY TRUSTS,
          however true it is. The Questions page proved that an hour earlier:
          fifteen questions, and he could not tell which he had approved.

          So it answers ONE question and refuses the rest — have we been through
          the whole document, and what did each part produce. All twenty chunks
          have a row INCLUDING the fifteen we have not read, because a coverage
          map that lists only what you have done cannot tell you what is left.

          Data lives in `doc_extracts` (sql/053), read through /api/doc-extracts
          — so walking a section and updating a row redraws this with no deploy.
          Hardcoding the map here would guarantee it drifts from the documents it
          summarises, which is the exact defect it exists to make visible. */}


      {/* ── 1b · THE SKILL MAP — Erez, 17 Aug: "I want you to create a diagram
          that helps us start to visualize each skill and what other skills it
          connects to… think about it as an entity diagram… let's start
          visualizing this before my mind blows up."

          It sits directly under the marked-up blueprint on purpose: reading a
          chunk of their document and updating this picture are the same act.
          A step appears here when their document specifies it, and not before —
          an invented step is worse than a thin box, because it looks settled.

          What it earned its place by showing: until today the five skills were
          FLAT, and a set of independent boxes needs no picture. §1's Visual Cost
          Intelligence Database produced the sixth, `Comparable Jobs`, which is
          the first skill that exists because two others call it. The moment one
          thing is shared, the shape of the system is something you can get
          wrong — and a picture is how you notice. */}
      <SectionBox title="The skills, as a map" layout="custom">
        <div style={{ marginTop: 0 }}>
          <OutformSkillMap />
        </div>
      </SectionBox>

      {/* ── 3 · DISCOVERY — the top section, by Erez's instruction.
          Everything we were given and everything we have sent them, on one
          shelf, in the order it happened. Their own document first, because it
          is the only item here we did not write.

          THE SIX, AND WHY EACH IS STILL HERE:
            their blueprint — Rev 10 verbatim. Ten internal drafts deep, a COO as
              executive sponsor, a CIO as technical sponsor. It is a scoped RFP
              wearing the clothes of an architecture document, and it is the
              specification we build against.
            the product and architecture — OUR document, sitting next to theirs
              exactly as he asked. One page now instead of five.
            the roadmap — the document Ariel asked for four separate times.
              THIS FRAME AND THE PDF READ THE SAME ROW: the generator at
              agents/quinn/outform-roadmap-pdf/build.mjs pulls its blocks from
              page_content at build time, so board, KB page and file on disk
              cannot disagree.
            the eleven topics — the ANSWERS to what they raised on acceptance,
              researched from their side of the table. Now the Tuesday agenda.
            the two emails — additive, newest last, and one that has been sent
              STAYS, because the thread is the record of how this account was
              won. Both are still unsent; nothing here reaches Outform without
              Erez.
            meeting 01 — the first thing on this board that came from them
              talking rather than from their document. Transcribed with
              faster-whisper, so the quotations are verbatim and timestamped. */}
      <SectionBox title="Discovery" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: 40, alignItems: "flex-start" }}>
          <PresentationFrame
            slidesBase={`${R2}/slides`}
            slideCount={24}
            pdf={`${R2}/outform-ai-estimating-blueprint-rev10.pdf`}
            label="Their blueprint · AI Estimating Tool · Architecture Blueprint Rev 10 · Apr 2026 · client document"
            width={1180}
            aspect={PAGE_ASPECT}
            caption="Outform's own document, unedited — 24 pages, ten internal drafts deep, with a COO as executive sponsor, a VP of Planning as program owner, a CIO as technical sponsor and a steering committee meeting monthly. They have already specified four engines (Ballpark · Detailed Estimate · Vendor Pricing Refresh · Market Comparison) over a visual database of ~1,500 past jobs, a conversion-probability model with published weights, four-tier RBAC, and integrations with D365, Sugar CRM, the Microsoft Data Lake and Smartsheet. Their targets: ballpark under 5 minutes against 1–4 days today, ±15% accuracy, +3–5% margin lift, +8–12% win rate."
          />

          <div style={{ display: "grid", gridTemplateColumns: `repeat(3, ${DOC_W}px)`, gap: 40, alignItems: "start" }}>
            <DocumentFrame
              slug="kb-doc/consultancy/outform/roadmap"
              label="The roadmap · the document we send to Outform · the PDF is built from this same row"
              width={DOC_W}
              height={DOC_H}
            />
            <DocumentFrame
              slug="kb-doc/consultancy/outform/the-eleven-topics"
              label="The eleven topics · the answers to what they raised on acceptance · now the Tuesday agenda"
              width={DOC_W}
              height={DOC_H}
            />
            <DocumentFrame
              slug="kb-doc/consultancy/outform/meeting-01"
              label="Meeting 01 · 10 Aug 2026 · 28 min · from the recording, not from memory"
              width={DOC_W}
              height={DOC_H}
            />
          </div>
        </div>
      </SectionBox>

      {/* ── 2 · THE PRODUCT — the concept as screens.

          Erez's brief, verbatim: "take option B, take the box panel, move it to
          the left. Then the PDF in the centre, then on the right have the actual
          chat interface, because the interaction is with the chat also. The user
          talks to the chat, and the chat updates the PDF and the boxes."

          Three columns, and the chat genuinely drives the other two: four
          scripted turns switch the active draft, open the detailed sheet, move
          the margin dial and widen the reset window, each marking the section it
          changed. Verified by clicking them with a real mouse, not by
          dispatching events.

          The document behind this screen is in Discovery, next to theirs. */}
      <SectionBox title="The product" layout="custom">
        <div style={{ marginTop: 0 }}>
          <OutformProductUX />
        </div>
      </SectionBox>

      {/* ── 3 · THE PRICING SHEET — the same instrument Hank built for Air Duct
          and Ruby reused for Everything 420. Interactive, not a document: every
          blue number is an input and the totals move.

          What makes this one different is the REQS column. All 468 requirements
          parsed out of their Rev 10 are assigned to exactly one row —
          machine-verified, 286 alpha and 182 full, none duplicated and none
          dropped. A steering committee cannot compare two quotes unless both are
          in the same units, and the only units that room already agrees on are
          the section numbers of their own document. */}
      <SectionBox title="What it costs" layout="custom">
        <div style={{ marginTop: 0 }}>
          <OutformPricingSheet />
        </div>
      </SectionBox>

      {/* ── 4 · MARKET RESEARCH — kept on Erez's word, 15 Aug ("the marketing you
          can keep"). Outform want to know whether this is a PRODUCT for other
          businesses, not only a tool for themselves, so the market gets its own
          shelf. Each wave lands as a KB page and is presented here; nothing is
          summarised into the board file itself. */}
      <SectionBox title="The market" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: 40, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/consultancy/outform/market-01-estimating-incumbents"
            label="01 · Estimating incumbents · every claim checked against the vendor's own site"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/market-02-adjacent-and-size"
            label="02 · Adjacent categories · and why the market-size numbers should not be quoted"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/market-03-pricing-research-money"
            label="03 · Pricing software, the research, and the money"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/market-04-verdict"
            label="04 · The verdict — is it a product, and what is the wedge"
            width={DOC_W}
            height={DOC_H}
          />
        </div>
      </SectionBox>
      {/* ── THE PRODUCT NOTEBOOK — 29 chapters, PRESENTED.

          ⛔ Erez, 1 Sept, twice: he could not find the notebook on a board. He
          was right, and the miss was worse than he thought — NOT ONE of these
          chapters had ever been presented anywhere. The section called "The
          product" above is a hand-built React component; the notebook itself
          lived only in the KB sidebar. So the document holding the ACM
          definition, the ten-step assembly order, the six unit populations and
          the rate-card screen was invisible on every board we own.

          Board law: write it in the KB, SHOW it on the board. Nothing here is
          summarised into this file — every frame is a page_content row, so an
          agent edits it with update_kb_page and this reflects it with no deploy.

          SIX ACROSS, and the number is arithmetic not taste: 6 x 880 + 5 x 24 =
          5400, plus SectionBox's 210 a side = 5820, inside the 6400 grid. Seven
          would be 6724 and would overflow. */}
      <SectionBox title="The product notebook" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexWrap: "wrap", gap: 24, maxWidth: 6 * DOC_W + 5 * 24 }}>
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/00-overview"
            label="00 · Overview & map — the honest state of every chapter"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/01-the-people"
            label="01 · The people — six roles, and seven more who never open an estimate"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/02-what-arrives"
            label="02 · What arrives — the Project Request, a ~40-field form"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/03-journey-ballpark"
            label="03 · Journey A · Ballpark — eleven moves, each with its oracle"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/04-journey-detailed"
            label="04 · Journey B · The detailed estimate"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/05-already-built"
            label="05 · What the old build taught us"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/06-the-agent"
            label="06 · The agent — it moves inputs, never invents a price"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/07-pricing"
            label="07 · Pricing — the four bands and the two directions"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/08-journey-reestimate"
            label="08 · Journey C · The re-estimate — the commonest job of all"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/09-confidence"
            label="09 · Confidence — four inputs, three computable"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/10-governance"
            label="10 · Governance — permissions, guardrails, the audit trail"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/11-logic-engine"
            label="11 · Business logic · the engine — ACM, the rates, the assembly order"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/12-logic-forms"
            label="12 · Business logic · the forms — the four-tier stack, six unit populations"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/13-the-database"
            label="13 · The database — results and settings, never calculations"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/14-settings"
            label="14 · Catalogues & Settings — where the ~50 numbers live"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/15-output-document"
            label="15 · The output document — internal and client from one record"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/16-engines"
            label="16 · The four engines — two driven, two on a clock"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/17-management"
            label="17 · Management surfaces"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/18-negotiation"
            label="18 · The negotiation surface"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/19-risk-schedule"
            label="19 · Risk & schedule"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/20-data-readiness"
            label="20 · Data readiness — and the win/loss loop"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/21-acceptance"
            label="21 · Acceptance — their criteria, and the instrument we lacked"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/22-the-wiki"
            label="22 · The wiki — the mitigation they specified themselves"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/23-when-it-goes-wrong"
            label="23 · When it goes wrong"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/24-terms-and-options"
            label="24 · Terms, exclusions & the option ladder"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/25-implementation-plan"
            label="25 · The implementation plan"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/26-questions-for-zaid"
            label="26 · Questions for Zaid"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/27-the-architecture"
            label="27 · The architecture — the diagram, the verdict, the decision"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/99-run-ledger"
            label="99 · Reasoning-run ledger"
            width={DOC_W}
            height={DOC_H}
          />
        </div>
      </SectionBox>

      {/* ── THE ARCHITECTURE — Atlas's set, brought onto the board beside the
          product it describes.

          Kept whole rather than summarised: every claim in it was measured
          against the live system, and five of its nineteen reasoning cycles
          killed a claim he had already reported as true.

          ⚠ 06 carries the set's strongest rebuild argument — "an ingest concept
          steering the pricing engine" — and the repository dismantled it HOURS
          after the page was written: families/ deleted, 1,651 lines, and the two
          surviving functions now carry a written rule that raw.* may be read for
          provenance or reconciliation and never to choose the arithmetic. The
          page is presented as written and its label says so; the correction
          lives in 01, which holds the decision. */}
      <SectionBox title="The architecture" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexWrap: "wrap", gap: 24, maxWidth: 6 * DOC_W + 5 * 24 }}>
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/00-overview"
            label="00 · Overview & map — Atlas's set, and how to read it"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/01-evolve-or-rebuild"
            label="01 · Evolve or rebuild — the run, the ledger, the DECISION"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/02-three-kinds-of-thing"
            label="02 · Three kinds of thing — FACT · POLICY · DERIVATION"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/06-the-database"
            label="06 · The database — 49 tables ⚠ its rebuild argument now superseded"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/07-governance"
            label="07 · Governance — 75% built and one table short"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/13-the-implementation-plan"
            label="13 · The implementation plan — 26 steps, each with an oracle"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/14-the-payload-contract"
            label="14 · The payload contract"
            width={DOC_W}
            height={DOC_H}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/architecture/20-screens-map"
            label="20 · Screens — eight exist, fourteen are required"
            width={DOC_W}
            height={DOC_H}
          />
        </div>
      </SectionBox>
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = { grid: GridNode as unknown as NodeTypes[string] };


/** One job: its measured anatomy beside the dossier Quinn wrote for it.
 *
 *  The pair is deliberate. The diagram is a COUNT — sheets, hidden sheets,
 *  formulas, what reached our tables — and the dossier is the READING of that
 *  job. Neither is much use alone: a count with no interpretation is trivia,
 *  and an interpretation with no count is an opinion.
 *
 *  ⛔ The diagram is board-native and the dossier is presented. Never the other
 *  way round, and never a presented page that itself carries a FlowDiagram —
 *  that nests a React Flow inside the board canvas and blanks the board. */
// ── ONE JOB, ONE COLUMN: page on top, entity diagram, tab distribution ──────
// All three at COLUMN WIDTH so eight columns read as a grid. The two diagrams
// are wrapped rather than modified: `Scaled` forces the SVG to the column width
// and lets its viewBox scale the height, so diagrams of very different natural
// sizes (1930x3236 for 240266, 3390x940 for 261801) still line up.
const JOB_COL_W = 720;
const JOB_COL_GAP = 26;

function Scaled({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{ width: JOB_COL_W, overflow: "hidden" }}
      ref={(el) => {
        if (!el) return;
        el.querySelectorAll("svg").forEach((svg) => {
          svg.setAttribute("width", "100%");
          svg.removeAttribute("height");
          (svg as SVGElement).style.height = "auto";
          (svg as SVGElement).style.display = "block";
        });
      }}
    >
      {children}
    </div>
  );
}

function JobColumn({
  job, label, slug, entity, tabs,
}: {
  job: string; label: string; slug: string;
  entity: React.ReactNode; tabs: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, width: JOB_COL_W }}>
      <div style={{ font: "600 15px/1.3 ui-sans-serif, system-ui", color: "#1c1c1c" }}>
        {job} · {label}
      </div>
      <DocumentFrame slug={slug} label={label} width={JOB_COL_W} height={JOB_DOC_H} />
      <Scaled>{entity}</Scaled>
      <Scaled>{tabs}</Scaled>
    </div>
  );
}

// Order: the six that share one cost chain, then the two that do not.
const JOB_COLUMNS = [
  { job: "240266", label: "Merit Beauty", slug: "kb-doc/consultancy/outform/jobs/240266-merit",
    entity: <Entity240266 />, tabs: <Job240266Diagram /> },
  { job: "243854", label: "Sephora Collection", slug: "kb-doc/consultancy/outform/jobs/243854-sephora",
    entity: <Entity243854 />, tabs: <Job243854Diagram /> },
  { job: "244524", label: "Lowe's", slug: "kb-doc/consultancy/outform/jobs/244524-lowes",
    entity: <Entity244524 />, tabs: <Job244524Diagram /> },
  { job: "255401", label: "Winky Lux", slug: "kb-doc/consultancy/outform/jobs/255401-winky-lux",
    entity: <Entity255401 />, tabs: <Job255401Diagram /> },
  { job: "261152", label: "L'Oreal", slug: "kb-doc/consultancy/outform/jobs/261152-loreal",
    entity: <Entity261152 />, tabs: <Job261152Diagram /> },
  { job: "261801", label: "NOYZ Fragrance", slug: "kb-doc/consultancy/outform/jobs/261801-noyz",
    entity: <Entity261801 />, tabs: <Job261801Diagram /> },
  { job: "250010", label: "LG Electronics", slug: "kb-doc/consultancy/outform/jobs/250010-lg",
    entity: <Entity250010 />, tabs: <Job250010Diagram /> },
  { job: "264714", label: "Morphe", slug: "kb-doc/consultancy/outform/jobs/264714-morphe",
    entity: <Entity264714 />, tabs: <Job264714Diagram /> },
];

function JobPair({
  diagram, slug, label,
}: { diagram: React.ReactNode; slug: string; label: string }) {
  return (
    <div style={{ display: "flex", gap: NOW_GAP, alignItems: "flex-start" }}>
      {diagram}
      <DocumentFrame slug={slug} label={label} width={JOB_DOC_W} height={JOB_DOC_H} />
    </div>
  );
}

// ⚠️ SIZED SO PAIRS SIT SIDE BY SIDE, NOT STACKED (Erez, 29 Aug): "put them next
// to each other, not under each other, because it becomes too big." A column of
// eleven pairs is 20,000px of board nobody scrolls to the end of; a wrapping row
// lets the whole family be read across in one pass, which is the comparison the
// section exists to make.
const JOB_DOC_W = 720;
const JOB_DOC_H = 900;

export function OutformBoardPage() {
  const nodes = useMemo<Node[]>(
    () => [{ id: "grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: {} }],
    [],
  );
  return (
    // data-build-salt is emitted into the bundle as a used string literal, so it
    // survives minification and guarantees a new content hash. Tree-shaking
    // cannot drop it and the minifier cannot fold it away.
    <div data-build-salt={BUILD_SALT} style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.1} />
    </div>
  );
}
